import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';

export interface MenuItem {
  key: string;
  label: ReactNode;
  /** Optional second line explaining what the item does (and does not do). */
  description?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  /** Marks the item for the page you are on. */
  current?: boolean;
}

export interface MenuProps {
  /** Trigger content, e.g. "Import" or a dots icon. A caret is added unless `hideCaret`. */
  label: ReactNode;
  items: MenuItem[];
  /** Required when the trigger is icon-only (the "⋯" button). */
  ariaLabel?: string;
  align?: 'left' | 'right';
  buttonClassName?: string;
  hideCaret?: boolean;
}

/** Button + popover list with arrow-key navigation. Used for "Import ▾" and "⋯". */
export default function Menu({ label, items, ariaLabel, align = 'left', buttonClassName = 'btn-secondary', hideCaret }: MenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();
  const buttonId = useId();

  const enabled = () => items.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0);

  const focusAt = (index: number) => itemRefs.current[index]?.focus();

  const close = (returnFocus = true) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  };

  // Focus the first enabled item whenever the menu opens.
  useEffect(() => {
    if (!open) return;
    const first = enabled()[0];
    if (first !== undefined) focusAt(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    const idxs = enabled();
    if (idxs.length === 0) return;
    const current = itemRefs.current.findIndex((el) => el === document.activeElement);
    const pos = idxs.indexOf(current);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusAt(idxs[(pos + 1) % idxs.length]!);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusAt(idxs[(pos - 1 + idxs.length) % idxs.length]!);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusAt(idxs[0]!);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusAt(idxs[idxs.length - 1]!);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={ariaLabel}
        className={buttonClassName}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {label}
        {!hideCaret && (
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3.5l3 3 3-3" />
          </svg>
        )}
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-labelledby={buttonId}
          onKeyDown={onMenuKeyDown}
          className={clsx(
            'absolute z-dropdown mt-1 max-w-[calc(100vw-2rem)] rounded-lg border border-slate-200 bg-white py-1 shadow-lg',
            items.some((it) => it.description) ? 'w-80' : 'min-w-[12rem]',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {items.map((item, i) => (
            <button
              key={item.key}
              ref={(el) => { itemRefs.current[i] = el; }}
              type="button"
              role="menuitem"
              aria-current={item.current ? 'page' : undefined}
              aria-describedby={item.description ? `${menuId}-${item.key}-desc` : undefined}
              disabled={item.disabled}
              tabIndex={-1}
              onClick={() => {
                close();
                item.onSelect();
              }}
              className={clsx(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm focus:bg-slate-100 focus:outline-none disabled:opacity-40',
                item.danger
                  ? 'text-danger-700 hover:bg-danger-50'
                  : item.current
                    ? 'bg-primary-50 font-semibold text-primary-700'
                    : 'text-slate-700 hover:bg-slate-50',
              )}
            >
              {item.icon}
              {item.description ? (
                <span className="flex flex-col gap-0.5">
                  <span>{item.label}</span>
                  <span id={`${menuId}-${item.key}-desc`} className="text-xs font-normal leading-snug text-slate-500">
                    {item.description}
                  </span>
                </span>
              ) : (
                item.label
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
